// Nota de Pedido — helpers compartidos (pedidos, form, detalle, reporte).
import { colors } from '../../design/tokens'

export const NP_ESTADOS = ['borrador', 'emitido', 'recibido', 'cerrado', 'reemplazado', 'anulado']
export const NP_ESTADO_LABEL = { borrador: 'Borrador', emitido: 'Emitida', recibido: 'Recibida', cerrado: 'Cancelada (saldo)', reemplazado: 'Reemplazada', anulado: 'Anulada' }
export const NP_ESTADO_TONO = { borrador: 'neutro', emitido: 'info', recibido: 'ok', cerrado: 'warn', reemplazado: 'neutro', anulado: 'err' }

export const MONEDAS = [{ codigo: 'ARS', nombre: 'Pesos' }, { codigo: 'USD', nombre: 'Dólares' }]
export const simboloMoneda = (m) => (m === 'USD' ? 'US$' : '$')

/** Motivos de CANCELACIÓN del saldo de un renglón (en LGR: motivo_cancelacion). */
export const MOTIVO_CIERRE = {
  proveedor_sin_stock: 'Proveedor sin stock',
  discontinuado: 'Discontinuado',
  proveedor_cancela: 'El proveedor cancela el saldo',
  ligier_cancela: 'Ligier cancela el saldo',
  reemplazada_por_np: 'Reemplazado por otra NP',
  acuerdo_comercial: 'Acuerdo comercial',
  otro: 'Otro (comentar)',
}

export const CONDICION_TIPO = {
  general: 'Descuento general del proveedor',
  producto: 'Descuento por producto',
  familia_marca: 'Descuento por familia / marca',
  bonificacion: 'Bonificación (lleva N, paga M)',
  cantidad: 'Descuento por cantidad',
  plazo_pago: 'Descuento por condición / plazo de pago',
  precio_especial: 'Precio especial',
  extraordinario: 'Descuento extraordinario',
  unica_operacion: 'Condición de esta sola operación',
}

const n = (v) => { const x = parseFloat(v); return Number.isFinite(x) ? x : 0 }
const r2 = (x) => Math.round(x * 100) / 100
const r4 = (x) => Math.round(x * 10000) / 10000

/**
 * Mismo cálculo que NotaPedidoService::calcularRenglon (regla de Martin, 1 sep 2026):
 *   precio = lista × (1−Dto1) × (1−Dto2) × (1−Dto3)          (cascada)
 *   neto   = precio × cantidad × (1 − bonificación general)   (la bonif. es de la CABECERA)
 *   iva    = neto × IVA%     ·  internos = neto × internos%
 *   total  = neto + iva + internos
 */
export function calcularRenglon(r, bonifPct = 0) {
  const cantidad = n(r.cantidad)
  const precio = r4(n(r.precio_lista) * (1 - n(r.descuento1_pct) / 100) * (1 - n(r.descuento2_pct) / 100) * (1 - n(r.descuento3_pct) / 100))
  const bruto = precio * cantidad
  const neto = r2(bruto * (1 - n(bonifPct) / 100))
  const iva = r2(neto * n(r.iva_pct === '' || r.iva_pct == null ? 21 : r.iva_pct) / 100)
  const internos = r2(neto * n(r.internos_pct) / 100)
  const final = r2(neto + iva + internos)
  return { precio, bruto: r2(bruto), neto, iva, internos, final }
}

export function totalesRenglones(renglones, bonifPct = 0) {
  return renglones.reduce((t, r) => {
    const c = calcularRenglon(r, bonifPct)
    return { neto: r2(t.neto + c.neto), iva: r2(t.iva + c.iva), internos: r2(t.internos + c.internos), final: r2(t.final + c.final) }
  }, { neto: 0, iva: 0, internos: 0, final: 0 })
}

/** Un renglón vale si tiene artículo resuelto, cantidad y precio de lista. Los vacíos (fila para tipear) no cuentan. */
export const renglonValido = (r) => !!r.sku && n(r.cantidad) > 0 && r.precio_lista !== '' && n(r.precio_lista) >= 0
export const renglonVacio = (r) => !r.sku && !r.codigo && !r.cantidad && !r.precio_lista

/** Renglón nuevo a partir de un artículo del maestro de LGR (IVA, internos, cód. proveedor y EAN se precargan de la ficha). */
export const renglonDesdeArticulo = (a, extra = {}) => ({
  sku: a.codigo_alfa, codigo: a.codigo_alfa, descripcion: a.descripcion, cantidad: '', precio_lista: '',
  descuento1_pct: '', descuento2_pct: '', descuento3_pct: '',
  iva_pct: a.iva != null && a.iva !== '' ? String(Number(a.iva)) : '21',
  internos_pct: Number(a.internos) ? String(Number(a.internos)) : '',
  codigo_proveedor: a.codigo_pro || '', ean: a.codigo_ean || '', origen_precio: 'manual', ...extra,
})

/** Fila vacía para tipear el código (grilla tipo Excel). */
export const renglonEnBlanco = () => ({ sku: '', codigo: '', descripcion: '', cantidad: '', precio_lista: '', descuento1_pct: '', descuento2_pct: '', descuento3_pct: '', iva_pct: '21', internos_pct: '', codigo_proveedor: '', ean: '', origen_precio: 'manual' })

/** Renglón del formulario a partir de un ítem guardado (edición). */
export const renglonDesdeItem = (it) => ({
  sku: it.sku, codigo: it.sku, descripcion: it.descripcion, cantidad: String(Number(it.cantidad)), precio_lista: String(Number(it.precio_lista)),
  descuento1_pct: Number(it.descuento1_pct ?? it.descuento_pct) ? String(Number(it.descuento1_pct ?? it.descuento_pct)) : '',
  descuento2_pct: Number(it.descuento2_pct) ? String(Number(it.descuento2_pct)) : '',
  descuento3_pct: Number(it.descuento3_pct) ? String(Number(it.descuento3_pct)) : '',
  iva_pct: String(Number(it.iva_pct)), internos_pct: Number(it.internos_pct) ? String(Number(it.internos_pct)) : '',
  codigo_proveedor: it.codigo_proveedor || '', ean: it.ean || '', origen_precio: it.origen_precio || 'manual',
})

/**
 * Color de la barra de avance de una línea (recibido / pedido). Misma paleta
 * que las barras de distribución: verde `progreso` para lo recibido, ámbar
 * `warnStrong` si parte del saldo se canceló.
 */
export function avanceLinea(it) {
  const ped = n(it.cantidad), rec = n(it.cantidad_recibida), cer = n(it.cantidad_cerrada)
  const pct = ped > 0 ? Math.min(100, Math.round(((rec + cer) / ped) * 100)) : 0
  const color = cer > 0 ? colors.warnStrong : colors.progreso
  return { pct, color }
}

/** "$1,234.56" / "US$1,234.56" — mismo formato que fmtMonto del resto de LGR. */
export const fmtMoneda = (v, moneda = 'ARS', dec = 2) => `${simboloMoneda(moneda)}${(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`
export const fmtPct = (v) => (Number(v) ? `${Number(v).toLocaleString('es-AR', { maximumFractionDigits: 2 })}%` : '—')
export const fmtCant = (v) => (v == null ? '—' : Number(v).toLocaleString('es-AR', { maximumFractionDigits: 3 }))
